/* eslint-disable prettier/prettier */
import './commitment-card.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import '@sola/locales/formatters.js';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import {
  ActionCard,
  ActionCardBody,
  ActionCardDivider,
  ActionCardFooter,
  ActionCardSection,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.js';
import {
  CommitmentDetailsButton,
  CommitmentDetailsSheet,
} from '../../organisms/commitments-details-sheet/commitment-details-sheet';
import {
  CommitmentCardOverrideData,
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from './utils';

type WithOverrideData = {
  overrideData: CommitmentCardOverrideData;
  commitment?: GraphQLCommitment | undefined;
};
type WithoutOverrideData = {
  overrideData?: never;
  commitment: GraphQLCommitment;
};

export type CommitmentCardProps = {
  variant?: 'default' | 'compact'; // NOTE: If we ever need one with a header we can add a 'full' variant
} & (WithOverrideData | WithoutOverrideData);

/**
 * Commitment card, shown for both commitments and giving history items.
 */
// TODO revisit handling of commitment/override data - possibly consolidate or make component more composable.
export const CommitmentCard: React.FC<CommitmentCardProps> = ({
  commitment,
  overrideData,
  variant = 'default',
}) => {
  const { t } = useTranslation(['my', 'common']);

  const isShowingFooter = variant !== 'compact';
  const isShowingFrequency =
    variant !== 'compact' && commitment?.paymentFrequency;
  const isShowingMissPayment =
    variant !== 'compact' && commitment?.hasMissedPayment;

  return (
    <ActionCard>
      <ActionCardSection>
        <ActionCardBody>
          <CommitmentAvatar responsive commitment={commitment} />

          <div className="CommitmentCard__titlegroup">
            {(overrideData?.title || commitment?.commitmentType) && (
              <p
                className={cn([
                  'CommitmentCard__title',
                  'label-potent label-md md:label-potent md:label-lg',
                  'mb-snug-md',
                ])}
              >
                {overrideData?.title ?? getCommitmentTitle(commitment!, t)}
              </p>
            )}
            {(overrideData?.label || commitment?.commitmentType) && (
              <p
                className={cn([
                  'CommitmentCard__description',
                  'label-potent label-sm label-neutral-mild md:label-md md:label-potent',
                ])}
              >
                {overrideData?.label ?? getCommitmentLabel(commitment!, t)}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-snug-lg md:gap-snug-xl items-end text-right">
            {(overrideData?.rate || commitment?.rate) && (
              <span className="label-potent label-md md:label-lg ">
                <span className="label-neutral-amplified">
                  {typeof overrideData?.rate === 'string'
                    ? overrideData.rate
                    : t('common:numbers.currency', {
                        amount: `${overrideData?.rate ?? commitment!.rate}`,
                        useNegativeParens: true,
                      })}
                </span>

                {isShowingFrequency && commitment.paymentFrequency && (
                  <span className="label-neutral-mild">
                    {getFrequencyAbbr(commitment.paymentFrequency, t)}
                  </span>
                )}
              </span>
            )}

            {isShowingMissPayment && (
              <Badge
                label={t('my:commitments.missedPaymentsLabel')}
                color="negative"
              />
            )}
          </div>
        </ActionCardBody>
      </ActionCardSection>

      {isShowingFooter && (
        <>
          <ActionCardDivider />

          <ActionCardFooter>
            {commitment?.paymentDateNext && (
              <span className="label-moderate label-neutral-mild label-sm md:label-md">
                {t('', {
                  date: commitment.paymentDateNext,
                })}
              </span>
            )}

            <CommitmentDetailsSheet
              openTrigger={
                <CommitmentDetailsButton>
                  {t('my:commitments.detailsSheet.triggerLabel')}
                </CommitmentDetailsButton>
              }
            >
              {/* NOTE: Child Details will go here */}
            </CommitmentDetailsSheet>
          </ActionCardFooter>
        </>
      )}
    </ActionCard>
  );
};
